import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  ColumnDef,
  ColumnFiltersState,
  ColumnOrderState,
  ColumnPinningState,
  ColumnSizingState,
  ExpandedState,
  FlexRenderDirective,
  GroupingState,
  PaginationState,
  RowPinningState,
  RowSelectionState,
  SortingState,
  VisibilityState,
  createAngularTable,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
} from '@tanstack/angular-table';
import { TableState, PaginationInfo, FilterOperator, TableIconSet, TableTheme, TableLabels, DEFAULT_TABLE_LABELS } from './tanstack-table.types';

/** Type for physical CSS position property */
type PhysicalPosition = 'left' | 'right';

@Component({
  selector: 'tanstack-table',
  templateUrl: './tanstack-table.component.html',
  styleUrl: './tanstack-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FlexRenderDirective],
  host: {
    '[class]': 'hostClasses()',
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class TanStackTableComponent<TData> {
  private elementRef = inject(ElementRef);
  private sanitizer = inject(DomSanitizer);
  // ========================================
  // Inputs
  // ========================================

  // Required data and columns
  data = input.required<TData[]>();
  columns = input.required<ColumnDef<TData, any>[]>();

  // Feature enablement
  enableSorting = input(true);
  enableMultiSort = input(true);
  enableFiltering = input(true);
  enableGlobalFilter = input(true);
  enablePagination = input(true);
  enableRowSelection = input(true);
  enableMultiRowSelection = input(true);
  enableColumnResizing = input(true);
  enableColumnOrdering = input(true);
  enableColumnPinning = input(true);
  enableColumnVisibility = input(true);
  enableGrouping = input(false);
  enableExpanding = input(false);
  enableRowPinning = input(false);

  // Layout options
  enableAutoRowHeight = input(false);

  // Pagination configuration
  initialPageSize = input(10);
  pageSizeOptions = input([10, 25, 50, 100]);

  // Sub-rows for hierarchical data
  getSubRows = input<(originalRow: TData, index: number) => TData[] | undefined>();

  // Row ID for selection tracking
  getRowId = input<(originalRow: TData, index: number) => string>();

  // Toolbar options
  showToolbar = input(true);
  showGlobalFilter = input(true);
  showColumnVisibility = input(true);

  // Pagination options
  showPagination = input(true);

  // Column sizing behavior
  initialAutoFitColumns = input(false);

  // Initial state (for persistence/SSR)
  initialState = input<Partial<TableState>>();

  // ========================================
  // Theming
  // ========================================

  // Theme selection - adds ngtt-theme-{name} class to host
  theme = input<TableTheme>('none');

  // Custom icons - partial override of default icons
  customIcons = input<Partial<TableIconSet>>();

  // ========================================
  // Internationalization (i18n)
  // ========================================

  // Custom labels - partial override of default labels
  labels = input<Partial<TableLabels>>();

  /** Merged labels (defaults + custom overrides) */
  mergedLabels = computed<TableLabels>(() => {
    const custom = this.labels();
    if (!custom) {
      return DEFAULT_TABLE_LABELS;
    }
    return { ...DEFAULT_TABLE_LABELS, ...custom };
  });

  /** Get localized filter operators based on current labels */
  localizedFilterOperators = computed(() => {
    const l = this.mergedLabels();
    return [
      { value: 'contains' as FilterOperator, label: l.operatorContains },
      { value: 'notContains' as FilterOperator, label: l.operatorNotContains },
      { value: 'equals' as FilterOperator, label: l.operatorEquals },
      { value: 'notEquals' as FilterOperator, label: l.operatorNotEquals },
      { value: 'startsWith' as FilterOperator, label: l.operatorStartsWith },
      { value: 'endsWith' as FilterOperator, label: l.operatorEndsWith },
      { value: 'greaterThan' as FilterOperator, label: l.operatorGreaterThan },
      { value: 'greaterThanOrEqual' as FilterOperator, label: l.operatorGreaterThanOrEqual },
      { value: 'lessThan' as FilterOperator, label: l.operatorLessThan },
      { value: 'lessThanOrEqual' as FilterOperator, label: l.operatorLessThanOrEqual },
      { value: 'isEmpty' as FilterOperator, label: l.operatorIsEmpty },
      { value: 'isNotEmpty' as FilterOperator, label: l.operatorIsNotEmpty },
    ];
  });

  /** Format pagination "showing rows" text with interpolation */
  formatShowingRows(start: number, end: number, total: number): string {
    return this.mergedLabels().showingRows
      .replace('{start}', String(start))
      .replace('{end}', String(end))
      .replace('{total}', String(total));
  }

  /** Format pagination "page info" text with interpolation */
  formatPageInfo(current: number, total: number): string {
    return this.mergedLabels().pageInfo
      .replace('{current}', String(current))
      .replace('{total}', String(total));
  }

  // ========================================
  // Default Icons
  // ========================================

  /** Default icon set - can be overridden via customIcons input */
  private readonly defaultIcons: TableIconSet = {
    // Sorting
    sortAsc: '↑',
    sortDesc: '↓',
    multiSortAsc: '↑+',
    multiSortDesc: '↓+',

    // Filter
    filter: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M4 4h16v2H4V4zm2 4h12v2H6V8zm2 4h8v2H8v-2zm2 4h4v2h-4v-2z"/></svg>',
    filterActive: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M4 4h16v2H4V4zm2 4h12v2H6V8zm2 4h8v2H8v-2zm2 4h4v2h-4v-2z"/></svg>',

    // Menu (three dots vertical)
    menu: '<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>',
    menuClose: '✕',

    // Pagination
    firstPage: '⏮',
    previousPage: '◀',
    nextPage: '▶',
    lastPage: '⏭',

    // Expand/Collapse
    expand: '▶',
    collapse: '▼',

    // Grouping
    groupBy: '⊞',
    ungroup: '⊟',

    // Column pinning (direction-agnostic aliases)
    pinStart: '◧',
    pinEnd: '◨',
    // Legacy names (kept for backwards compatibility)
    pinLeft: '◧',
    pinRight: '◨',
    unpin: '⊗',

    // Row pinning
    pinTop: '⬆',
    pinBottom: '⬇',
    pinned: '📌',

    // Actions
    clear: '⊘',
    hide: '◻',
    close: '✕',
  };

  /** Merged icons (raw strings, defaults + custom overrides) */
  private mergedIcons = computed<TableIconSet>(() => {
    const custom = this.customIcons();
    if (!custom) {
      return this.defaultIcons;
    }
    return { ...this.defaultIcons, ...custom };
  });

  /** Sanitized icons safe for innerHTML binding */
  icons = computed(() => {
    const rawIcons = this.mergedIcons();
    const sanitized: Record<string, SafeHtml> = {};
    for (const [key, value] of Object.entries(rawIcons)) {
      sanitized[key] = this.sanitizer.bypassSecurityTrustHtml(value);
    }
    return sanitized as { [K in keyof TableIconSet]: SafeHtml };
  });

  /** Host classes including theme class */
  hostClasses = computed(() => {
    const themeName = this.theme();
    if (themeName && themeName !== 'none') {
      return `ngtt-theme-${themeName}`;
    }
    return '';
  });

  // ========================================
  // Outputs
  // ========================================

  rowSelectionChanged = output<TData[]>();
  stateChanged = output<Partial<TableState>>();

  // ========================================
  // State Signals
  // ========================================

  sorting = signal<SortingState>([]);
  columnFilters = signal<ColumnFiltersState>([]);
  globalFilter = signal<string>('');
  pagination = signal<PaginationState>({
    pageIndex: 0,
    pageSize: this.initialPageSize(),
  });
  rowSelection = signal<RowSelectionState>({});
  columnVisibility = signal<VisibilityState>({});
  columnOrder = signal<ColumnOrderState>([]);
  columnPinning = signal<ColumnPinningState>({ left: [], right: [] });
  columnSizing = signal<ColumnSizingState>({});
  expanded = signal<ExpandedState>({});
  grouping = signal<GroupingState>([]);
  rowPinning = signal<RowPinningState>({ top: [], bottom: [] });

  // Filter operators per column
  filterOperators = signal<Record<string, FilterOperator>>({});

  // Available filter operators for UI
  readonly availableFilterOperators: { value: FilterOperator; label: string }[] = [
    { value: 'contains', label: 'Contains' },
    { value: 'notContains', label: 'Not Contains' },
    { value: 'equals', label: 'Equals' },
    { value: 'notEquals', label: 'Not Equals' },
    { value: 'startsWith', label: 'Starts With' },
    { value: 'endsWith', label: 'Ends With' },
    { value: 'greaterThan', label: 'Greater Than' },
    { value: 'greaterThanOrEqual', label: 'Greater Than or Equal' },
    { value: 'lessThan', label: 'Less Than' },
    { value: 'lessThanOrEqual', label: 'Less Than or Equal' },
    { value: 'isEmpty', label: 'Is Empty' },
    { value: 'isNotEmpty', label: 'Is Not Empty' },
  ];

  // ========================================
  // View References
  // ========================================

  tableWrapper = viewChild<ElementRef<HTMLDivElement>>('tableWrapper');

  // ========================================
  // RTL Detection
  // ========================================

  /** Signal tracking whether the component is in RTL mode */
  private isRtl = signal(false);

  /** Check and update RTL state based on host element's computed direction */
  private updateRtlState() {
    const element = this.elementRef.nativeElement;
    const direction = getComputedStyle(element).direction;
    this.isRtl.set(direction === 'rtl');
  }

  /** Get the physical CSS position property for the select column (pinned at start) */
  selectColumnPosition = computed<PhysicalPosition>(() => {
    return this.isRtl() ? 'right' : 'left';
  });

  // ========================================
  // Table Instance
  // ========================================

  // Wrap columns to add advanced filterFn to each
  private columnsWithFilterFn = computed(() => {
    return this.columns().map(col => ({
      ...col,
      filterFn: col.filterFn ?? 'advanced', // Use advanced filter unless already specified
    })) as ColumnDef<TData, any>[];
  });

  table = createAngularTable<TData>(() => ({
    data: this.data(),
    columns: this.columnsWithFilterFn(),

    // State
    state: {
      sorting: this.sorting(),
      columnFilters: this.columnFilters(),
      globalFilter: this.globalFilter(),
      pagination: this.pagination(),
      rowSelection: this.rowSelection(),
      columnVisibility: this.columnVisibility(),
      columnOrder: this.columnOrder(),
      columnPinning: this.columnPinning(),
      columnSizing: this.columnSizing(),
      expanded: this.expanded(),
      grouping: this.grouping(),
      rowPinning: this.rowPinning(),
    },

    // State updaters
    onSortingChange: (updater) => {
      this.sorting.update((old) => (typeof updater === 'function' ? updater(old) : updater));
    },
    onColumnFiltersChange: (updater) => {
      this.columnFilters.update((old) =>
        typeof updater === 'function' ? updater(old) : updater
      );
    },
    onGlobalFilterChange: (updater) => {
      this.globalFilter.update((old) =>
        typeof updater === 'function' ? updater(old) : updater
      );
    },
    onPaginationChange: (updater) => {
      this.pagination.update((old) => (typeof updater === 'function' ? updater(old) : updater));
    },
    onRowSelectionChange: (updater) => {
      this.rowSelection.update((old) =>
        typeof updater === 'function' ? updater(old) : updater
      );
    },
    onColumnVisibilityChange: (updater) => {
      this.columnVisibility.update((old) =>
        typeof updater === 'function' ? updater(old) : updater
      );
    },
    onColumnOrderChange: (updater) => {
      this.columnOrder.update((old) =>
        typeof updater === 'function' ? updater(old) : updater
      );
    },
    onColumnPinningChange: (updater) => {
      this.columnPinning.update((old) =>
        typeof updater === 'function' ? updater(old) : updater
      );
    },
    onColumnSizingChange: (updater) => {
      this.columnSizing.update((old) =>
        typeof updater === 'function' ? updater(old) : updater
      );
    },
    onExpandedChange: (updater) => {
      this.expanded.update((old) => (typeof updater === 'function' ? updater(old) : updater));
    },
    onGroupingChange: (updater) => {
      this.grouping.update((old) => (typeof updater === 'function' ? updater(old) : updater));
    },
    onRowPinningChange: (updater) => {
      this.rowPinning.update((old) => (typeof updater === 'function' ? updater(old) : updater));
    },

    // Row models
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: this.enableSorting() ? getSortedRowModel() : undefined,
    getFilteredRowModel: this.enableFiltering() ? getFilteredRowModel() : undefined,
    getPaginationRowModel: this.enablePagination() ? getPaginationRowModel() : undefined,
    getExpandedRowModel: (this.enableExpanding() || this.enableGrouping()) ? getExpandedRowModel() : undefined,
    getGroupedRowModel: this.enableGrouping() ? getGroupedRowModel() : undefined,
    getFacetedRowModel: this.enableFiltering() ? getFacetedRowModel() : undefined,
    getFacetedUniqueValues: this.enableFiltering() ? getFacetedUniqueValues() : undefined,
    getFacetedMinMaxValues: this.enableFiltering() ? getFacetedMinMaxValues() : undefined,

    // Feature options
    enableSorting: this.enableSorting(),
    enableMultiSort: this.enableMultiSort(),
    enableFiltering: this.enableFiltering(),
    enableGlobalFilter: this.enableGlobalFilter(),
    enableRowSelection: this.enableRowSelection(),
    enableMultiRowSelection: this.enableMultiRowSelection(),
    enableColumnResizing: this.enableColumnResizing(),
    enableColumnPinning: this.enableColumnPinning(),
    enableGrouping: this.enableGrouping(),
    enableExpanding: this.enableExpanding() || this.enableGrouping(),
    enableRowPinning: this.enableRowPinning(),

    // Global filter function
    globalFilterFn: 'includesString',

    // Custom filter functions for advanced operators
    filterFns: {
      advanced: this.advancedFilterFn.bind(this),
    },

    // Column resizing options
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: 60,
      maxSize: 800,
    },

    // Sub-rows
    getSubRows: this.getSubRows() as ((originalRow: TData, index: number) => TData[] | undefined) | undefined,

    // Row ID
    getRowId: this.getRowId() as ((originalRow: TData, index: number) => string) | undefined,
  }));

  // ========================================
  // Computed Properties
  // ========================================

  // Column sizes for CSS variables (performance optimization)
  columnSizingInfo = computed(() => this.table.getState().columnSizing);
  columnSizingVars = computed(() => {
    // Trigger reactivity when column sizing changes
    this.columnSizingInfo();
    // Also trigger reactivity when column visibility changes
    this.table.getState().columnVisibility;

    // Only get visible leaf headers
    const headers = this.table.getLeafHeaders();
    const vars: Record<string, string> = {};

    headers.forEach((header) => {
      // Only include visible columns
      if (header.column.getIsVisible()) {
        const size = header.getSize();
        vars[`--header-${header.id}-size`] = `${size}px`;
        vars[`--col-${header.column.id}-size`] = `${size}px`;
      }
    });

    return vars;
  });

  // Get all header groups
  headerGroups = computed(() => this.table.getHeaderGroups());

  // Get visible columns (for row-by-row rendering)
  visibleColumns = computed(() => {
    // Trigger reactivity on visibility changes
    this.table.getState().columnVisibility;

    const leftCols = this.table.getLeftLeafColumns();
    const centerCols = this.table.getCenterLeafColumns();
    const rightCols = this.table.getRightLeafColumns();

    // Filter to only include visible columns
    const allCols = [...leftCols, ...centerCols, ...rightCols];
    return allCols.filter(col => col.getIsVisible());
  });

  // Map columns to their headers for accessing header methods like resize
  columnHeaders = computed(() => {
    const headers = this.table.getFlatHeaders();
    const map = new Map();
    headers.forEach((header) => {
      map.set(header.column.id, header);
    });
    return map;
  });

  // Get rows based on pagination, properly ordered with pinned rows
  rows = computed(() => {
    const rowModel = this.table.getRowModel();

    // If row pinning is enabled, order rows: top pinned, center, bottom pinned
    if (this.enableRowPinning()) {
      const topPinned = this.rowPinning().top ?? [];
      const bottomPinned = this.rowPinning().bottom ?? [];

      const topRows: typeof rowModel.rows = [];
      const centerRows: typeof rowModel.rows = [];
      const bottomRows: typeof rowModel.rows = [];

      for (const row of rowModel.rows) {
        if (topPinned.includes(row.id)) {
          topRows.push(row);
        } else if (bottomPinned.includes(row.id)) {
          bottomRows.push(row);
        } else {
          centerRows.push(row);
        }
      }

      return [...topRows, ...centerRows, ...bottomRows];
    }

    return rowModel.rows;
  });

  // Get pinned column positions for sticky styling
  // Handles RTL by swapping physical left/right properties:
  // - In LTR: "pin left" uses CSS left, "pin right" uses CSS right
  // - In RTL: "pin left" uses CSS right, "pin right" uses CSS left
  pinnedColumnPositions = computed(() => {
    const leftPinned = this.table.getLeftLeafColumns();
    const rightPinned = this.table.getRightLeafColumns();
    const rtl = this.isRtl();

    // In RTL mode, swap the physical CSS properties
    // "Pin Left" (logical start) -> physical right in RTL
    // "Pin Right" (logical end) -> physical left in RTL
    const startPosition: PhysicalPosition = rtl ? 'right' : 'left';
    const endPosition: PhysicalPosition = rtl ? 'left' : 'right';

    const positions: Record<string, { position: PhysicalPosition; offset: string }> = {};

    // Calculate start positions (cumulative widths from start edge)
    // Start with 48px offset if row selection is enabled (auto-select column is pinned at start)
    let startOffset = this.enableRowSelection() ? 48 : 0;
    for (const col of leftPinned) {
      positions[col.id] = { position: startPosition, offset: `${startOffset}px` };
      startOffset += col.getSize();
    }

    // Calculate end positions (cumulative widths from end edge)
    let endOffset = 0;
    for (let i = rightPinned.length - 1; i >= 0; i--) {
      const col = rightPinned[i];
      positions[col.id] = { position: endPosition, offset: `${endOffset}px` };
      endOffset += col.getSize();
    }

    return positions;
  });

  // Check if a column is the first data column (for row pin controls and depth indentation)
  // Note: 'select' column is now auto-generated, so we only check for 'expander'
  isFirstDataColumn(columnId: string): boolean {
    const cols = this.visibleColumns();
    for (const col of cols) {
      if (col.id !== 'expander') {
        return col.id === columnId;
      }
    }
    return false;
  }

  /**
   * Calculate minimum width for a column header based on enabled features
   * This ensures all header controls are visible when their features are enabled
   */
  getColumnMinWidth(column: any): string {
    // Special columns have smaller min-width
    if (column.id === 'select' || column.id === 'expander') {
      return '48px';
    }

    // Base width for title and sort indicator
    let minWidth = 60; // Base title width

    // Add space for sort indicator if sorting is enabled on this column
    if (this.enableSorting() && column.getCanSort()) {
      minWidth += 24; // Sort icon + potential multi-sort index
    }

    // Add space for header actions (filter button + menu button)
    minWidth += 8; // Gap between label and actions

    // Menu button is always shown
    minWidth += 26; // Menu button width + gap

    // Filter button if filtering is enabled for this column
    if (this.enableFiltering() && column.getCanFilter()) {
      minWidth += 26; // Filter button width + gap
    }

    // Add padding
    minWidth += 32; // Left and right padding (16px each)

    return `${minWidth}px`;
  }

  // Pagination info
  paginationInfo = computed<PaginationInfo>(() => {
    const state = this.table.getState().pagination;
    const totalRows = this.table.getFilteredRowModel().rows.length;
    const totalPages = this.table.getPageCount();
    const startRow = state.pageIndex * state.pageSize + 1;
    const endRow = Math.min(startRow + state.pageSize - 1, totalRows);

    return {
      currentPage: state.pageIndex + 1,
      totalPages,
      pageSize: state.pageSize,
      totalRows,
      startRow,
      endRow,
      canPreviousPage: this.table.getCanPreviousPage(),
      canNextPage: this.table.getCanNextPage(),
    };
  });

  // Selected rows
  selectedRows = computed(() => {
    const selection = this.table.getSelectedRowModel().rows;
    return selection.map((row) => row.original);
  });

  // ========================================
  // Column Ordering State
  // ========================================

  draggedColumnId = signal<string | null>(null);
  dropTargetColumnId = signal<string | null>(null);

  // Column menu open state
  columnMenuOpenId = signal<string | null>(null);

  // Filter popover open state
  filterPopoverColumnId = signal<string | null>(null);

  // ========================================
  // Effects
  // ========================================

  constructor() {
    // Apply initial state if provided
    const initState = this.initialState();
    if (initState) {
      if (initState.sorting !== undefined) this.sorting.set(initState.sorting);
      if (initState.columnFilters !== undefined) this.columnFilters.set(initState.columnFilters);
      if (initState.globalFilter !== undefined) this.globalFilter.set(initState.globalFilter);
      if (initState.pagination !== undefined) this.pagination.set(initState.pagination);
      if (initState.rowSelection !== undefined) this.rowSelection.set(initState.rowSelection);
      if (initState.columnVisibility !== undefined) this.columnVisibility.set(initState.columnVisibility);
      if (initState.columnOrder !== undefined) this.columnOrder.set(initState.columnOrder);
      if (initState.columnPinning !== undefined) this.columnPinning.set(initState.columnPinning);
      if (initState.columnSizing !== undefined) this.columnSizing.set(initState.columnSizing);
      if (initState.expanded !== undefined) this.expanded.set(initState.expanded);
      if (initState.grouping !== undefined) this.grouping.set(initState.grouping);
      if (initState.rowPinning !== undefined) this.rowPinning.set(initState.rowPinning);
    }

    // Emit selected rows when selection changes
    effect(() => {
      const selected = this.selectedRows();
      untracked(() => {
        this.rowSelectionChanged.emit(selected);
      });
    });

    // Emit state changes
    effect(() => {
      const state: Partial<TableState> = {
        sorting: this.sorting(),
        columnFilters: this.columnFilters(),
        globalFilter: this.globalFilter(),
        pagination: this.pagination(),
        rowSelection: this.rowSelection(),
        columnVisibility: this.columnVisibility(),
        columnOrder: this.columnOrder(),
        columnPinning: this.columnPinning(),
        columnSizing: this.columnSizing(),
        expanded: this.expanded(),
        grouping: this.grouping(),
        rowPinning: this.rowPinning(),
      };

      untracked(() => {
        this.stateChanged.emit(state);
      });
    });

    // Detect RTL mode after render
    afterNextRender(() => {
      this.updateRtlState();
    });

    // Initial auto-fit columns to available width
    afterNextRender(() => {
      if (!this.initialAutoFitColumns()) {
        return;
      }

      const wrapper = this.tableWrapper();
      if (!wrapper) {
        return;
      }

      const element = wrapper.nativeElement;
      const columns = this.table.getAllLeafColumns();

      // Get default sizes for all columns
      const defaultSizes = columns.map(col => col.getSize());
      const totalDefaultSize = defaultSizes.reduce((sum, size) => sum + size, 0);

      // Calculate available width (clientWidth excludes borders and scrollbars)
      const containerWidth = element.clientWidth;

      // If total default size is less than container width, distribute the extra space
      if (totalDefaultSize < containerWidth) {
        const newSizing: ColumnSizingState = {};
        let allocatedWidth = 0;

        columns.forEach((col, index) => {
          // Calculate proportional width based on default size
          const proportion = defaultSizes[index] / totalDefaultSize;

          // For all columns except the last, use Math.floor
          // For the last column, use remaining width to ensure exact fit
          if (index === columns.length - 1) {
            newSizing[col.id] = containerWidth - allocatedWidth;
          } else {
            const newSize = Math.floor(containerWidth * proportion);
            newSizing[col.id] = newSize;
            allocatedWidth += newSize;
          }
        });

        // Apply the new sizing
        this.columnSizing.set(newSizing);
      }
    });
  }

  // ========================================
  // Public Methods
  // ========================================

  /**
   * Reset all filters
   */
  resetFilters() {
    this.columnFilters.set([]);
    this.globalFilter.set('');
    this.filterOperators.set({});
  }

  /**
   * Reset sorting
   */
  resetSorting() {
    this.sorting.set([]);
  }

  /**
   * Handle column sorting with multi-sort support
   * Ctrl+Click (Windows/Linux) or Cmd+Click (Mac) enables multi-sort
   */
  handleSort(column: any, event: MouseEvent) {
    if (!column.getCanSort()) return;

    // Check for multi-sort modifier key (Ctrl on Windows/Linux, Cmd on Mac)
    const isMulti = this.enableMultiSort() && (event.ctrlKey || event.metaKey);

    // Toggle sorting: undefined -> asc -> desc -> undefined
    // For multi-sort, add to existing sorts instead of replacing
    column.toggleSorting(undefined, isMulti);
  }

  /**
   * Reset grouping
   */
  resetGrouping() {
    this.grouping.set([]);
    this.expanded.set({});
  }

  /**
   * Toggle column grouping
   */
  toggleColumnGrouping(columnId: string) {
    const currentGrouping = this.grouping();
    const index = currentGrouping.indexOf(columnId);

    if (index >= 0) {
      // Remove from grouping
      const newGrouping = currentGrouping.filter(id => id !== columnId);
      this.grouping.set(newGrouping);
    } else {
      // Add to grouping
      this.grouping.set([...currentGrouping, columnId]);
    }
  }

  /**
   * Check if column is currently grouped
   */
  isColumnGrouped(columnId: string): boolean {
    return this.grouping().includes(columnId);
  }

  // ========================================
  // Column Pinning Methods
  // ========================================

  /**
   * Pin column to start (left in LTR, right in RTL)
   * This is the direction-agnostic way to pin columns
   */
  pinColumnStart(columnId: string) {
    // In RTL, start is visual right (TanStack's left)
    // In LTR, start is visual left (TanStack's left)
    this.pinToTanStackLeft(columnId);
  }

  /**
   * Pin column to end (right in LTR, left in RTL)
   * This is the direction-agnostic way to pin columns
   */
  pinColumnEnd(columnId: string) {
    // In RTL, end is visual left (TanStack's right)
    // In LTR, end is visual right (TanStack's right)
    this.pinToTanStackRight(columnId);
  }

  /**
   * Pin column to visual left side of the screen
   * In LTR: uses TanStack's left
   * In RTL: uses TanStack's right (because visual left = TanStack right in RTL)
   */
  pinColumnLeft(columnId: string) {
    if (this.isRtl()) {
      this.pinToTanStackRight(columnId);
    } else {
      this.pinToTanStackLeft(columnId);
    }
  }

  /**
   * Pin column to visual right side of the screen
   * In LTR: uses TanStack's right
   * In RTL: uses TanStack's left (because visual right = TanStack left in RTL)
   */
  pinColumnRight(columnId: string) {
    if (this.isRtl()) {
      this.pinToTanStackLeft(columnId);
    } else {
      this.pinToTanStackRight(columnId);
    }
  }

  /**
   * Internal: Pin to TanStack's left array
   */
  private pinToTanStackLeft(columnId: string) {
    const current = this.columnPinning();
    const left = current.left ?? [];
    const right = current.right ?? [];

    // Remove from right if pinned there
    const newRight = right.filter(id => id !== columnId);

    // Add to left if not already there
    if (!left.includes(columnId)) {
      this.columnPinning.set({
        left: [...left, columnId],
        right: newRight,
      });
    }
  }

  /**
   * Internal: Pin to TanStack's right array
   */
  private pinToTanStackRight(columnId: string) {
    const current = this.columnPinning();
    const left = current.left ?? [];
    const right = current.right ?? [];

    // Remove from left if pinned there
    const newLeft = left.filter(id => id !== columnId);

    // Add to right if not already there
    if (!right.includes(columnId)) {
      this.columnPinning.set({
        left: newLeft,
        right: [...right, columnId],
      });
    }
  }

  /**
   * Unpin column
   */
  unpinColumn(columnId: string) {
    const current = this.columnPinning();
    this.columnPinning.set({
      left: (current.left ?? []).filter(id => id !== columnId),
      right: (current.right ?? []).filter(id => id !== columnId),
    });
  }

  /**
   * Get column pin state
   */
  getColumnPinState(columnId: string): 'left' | 'right' | false {
    const current = this.columnPinning();
    if ((current.left ?? []).includes(columnId)) return 'left';
    if ((current.right ?? []).includes(columnId)) return 'right';
    return false;
  }

  // ========================================
  // Row Pinning Methods
  // ========================================

  /**
   * Pin row to top
   */
  pinRowTop(rowId: string) {
    const current = this.rowPinning();
    const top = current.top ?? [];
    const bottom = current.bottom ?? [];

    // Remove from bottom if pinned there
    const newBottom = bottom.filter(id => id !== rowId);

    // Add to top if not already there
    if (!top.includes(rowId)) {
      this.rowPinning.set({
        top: [...top, rowId],
        bottom: newBottom,
      });
    }
  }

  /**
   * Pin row to bottom
   */
  pinRowBottom(rowId: string) {
    const current = this.rowPinning();
    const top = current.top ?? [];
    const bottom = current.bottom ?? [];

    // Remove from top if pinned there
    const newTop = top.filter(id => id !== rowId);

    // Add to bottom if not already there
    if (!bottom.includes(rowId)) {
      this.rowPinning.set({
        top: newTop,
        bottom: [...bottom, rowId],
      });
    }
  }

  /**
   * Unpin row
   */
  unpinRow(rowId: string) {
    const current = this.rowPinning();
    this.rowPinning.set({
      top: (current.top ?? []).filter(id => id !== rowId),
      bottom: (current.bottom ?? []).filter(id => id !== rowId),
    });
  }

  /**
   * Get row pin state
   */
  getRowPinState(rowId: string): 'top' | 'bottom' | false {
    const current = this.rowPinning();
    if ((current.top ?? []).includes(rowId)) return 'top';
    if ((current.bottom ?? []).includes(rowId)) return 'bottom';
    return false;
  }

  /**
   * Reset column pinning
   */
  resetColumnPinning() {
    this.columnPinning.set({ left: [], right: [] });
  }

  /**
   * Reset row pinning
   */
  resetRowPinning() {
    this.rowPinning.set({ top: [], bottom: [] });
  }

  // ========================================
  // Advanced Filter Methods
  // ========================================

  /**
   * Set filter operator for a column
   */
  setFilterOperator(columnId: string, operator: FilterOperator) {
    this.filterOperators.update((ops) => ({ ...ops, [columnId]: operator }));
    // Re-apply the current filter value with new operator
    const column = this.table.getColumn(columnId);
    if (column) {
      const currentValue = column.getFilterValue();
      if (currentValue !== undefined && currentValue !== '') {
        // Trigger re-filter by setting value again
        column.setFilterValue(currentValue);
      }
    }
  }

  /**
   * Get filter operator for a column
   */
  getFilterOperator(columnId: string): FilterOperator {
    return this.filterOperators()[columnId] ?? 'contains';
  }

  /**
   * Apply advanced filter with operator
   */
  applyAdvancedFilter(columnId: string, value: string) {
    const operator = this.getFilterOperator(columnId);
    const column = this.table.getColumn(columnId);

    if (!column) return;

    // For isEmpty/isNotEmpty, we don't need a value
    if (operator === 'isEmpty' || operator === 'isNotEmpty') {
      column.setFilterValue({ operator, value: '' });
      return;
    }

    // For other operators, store both operator and value
    if (value === '' || value === undefined) {
      column.setFilterValue(undefined);
    } else {
      column.setFilterValue({ operator, value });
    }
  }

  /**
   * Get display value for a column filter (extract from advanced filter object)
   */
  getFilterDisplayValue(column: any): string {
    const filterValue = column.getFilterValue();
    if (filterValue === undefined || filterValue === null) return '';
    if (typeof filterValue === 'object' && 'value' in filterValue) {
      return filterValue.value ?? '';
    }
    return String(filterValue);
  }

  // ========================================
  // Column Menu Methods
  // ========================================

  /**
   * Toggle column menu
   */
  toggleColumnMenu(columnId: string, event: Event) {
    event.stopPropagation();
    const current = this.columnMenuOpenId();
    this.columnMenuOpenId.set(current === columnId ? null : columnId);
  }

  /**
   * Close column menu
   */
  closeColumnMenu() {
    this.columnMenuOpenId.set(null);
  }

  // ========================================
  // Filter Popover Methods
  // ========================================

  /**
   * Toggle filter popover for a column
   */
  toggleFilterPopover(columnId: string, event: Event) {
    event.stopPropagation();
    // Close column menu if open
    this.columnMenuOpenId.set(null);
    const current = this.filterPopoverColumnId();
    this.filterPopoverColumnId.set(current === columnId ? null : columnId);
  }

  /**
   * Close filter popover
   */
  closeFilterPopover() {
    this.filterPopoverColumnId.set(null);
  }

  /**
   * Handle document clicks to close popovers when clicking outside
   */
  onDocumentClick(event: MouseEvent) {
    // Close column menu and filter popover when clicking outside
    if (this.columnMenuOpenId() !== null || this.filterPopoverColumnId() !== null) {
      const target = event.target as HTMLElement;
      // Check if click is inside a popover or menu
      const isInsidePopover = target.closest('.column-menu-dropdown, .filter-popover, .header-actions');
      if (!isInsidePopover) {
        this.closeColumnMenu();
        this.closeFilterPopover();
      }
    }
  }

  /**
   * Check if there are any reset actions available
   */
  hasAnyResetAction(): boolean {
    const pinning = this.columnPinning();
    const hasPinning = (pinning.left?.length ?? 0) > 0 || (pinning.right?.length ?? 0) > 0;
    return (
      (this.enableFiltering() && this.columnFilters().length > 0) ||
      (this.enableSorting() && this.sorting().length > 0) ||
      (this.enableGrouping() && this.grouping().length > 0) ||
      (this.enableColumnPinning() && hasPinning)
    );
  }

  /**
   * Check if a column has an active filter
   */
  hasColumnFilter(columnId: string): boolean {
    const column = this.table.getColumn(columnId);
    if (!column) return false;
    const filterValue = column.getFilterValue();
    if (filterValue === undefined || filterValue === null) return false;
    if (typeof filterValue === 'object' && 'operator' in filterValue) {
      const typedFilter = filterValue as { operator: string; value?: string };
      // For isEmpty/isNotEmpty, filter is active even without value
      if (typedFilter.operator === 'isEmpty' || typedFilter.operator === 'isNotEmpty') {
        return true;
      }
      return typedFilter.value !== undefined && typedFilter.value !== '';
    }
    return String(filterValue) !== '';
  }

  /**
   * Hide column via menu
   */
  hideColumn(columnId: string) {
    const column = this.table.getColumn(columnId);
    if (column) {
      column.toggleVisibility(false);
    }
    this.closeColumnMenu();
  }

  /**
   * Clear filter for a specific column via menu
   */
  clearColumnFilter(columnId: string) {
    const column = this.table.getColumn(columnId);
    if (column) {
      column.setFilterValue(undefined);
    }
    // Also reset the operator
    this.filterOperators.update((ops) => {
      const newOps = { ...ops };
      delete newOps[columnId];
      return newOps;
    });
    this.closeColumnMenu();
  }

  /**
   * Custom filter function for advanced operators
   */
  advancedFilterFn(row: any, columnId: string, filterValue: any): boolean {
    if (filterValue === undefined || filterValue === null) return true;

    // Handle legacy string filter values (backwards compatibility)
    let operator: FilterOperator = 'contains';
    let value: string = '';

    if (typeof filterValue === 'object' && 'operator' in filterValue) {
      operator = filterValue.operator;
      value = filterValue.value ?? '';
    } else {
      value = String(filterValue);
    }

    const cellValue = row.getValue(columnId);
    const cellStr = cellValue !== null && cellValue !== undefined ? String(cellValue).toLowerCase() : '';
    const filterStr = value.toLowerCase();

    switch (operator) {
      case 'equals':
        return cellStr === filterStr;
      case 'notEquals':
        return cellStr !== filterStr;
      case 'contains':
        return cellStr.includes(filterStr);
      case 'notContains':
        return !cellStr.includes(filterStr);
      case 'startsWith':
        return cellStr.startsWith(filterStr);
      case 'endsWith':
        return cellStr.endsWith(filterStr);
      case 'greaterThan':
        return parseFloat(cellStr) > parseFloat(filterStr);
      case 'greaterThanOrEqual':
        return parseFloat(cellStr) >= parseFloat(filterStr);
      case 'lessThan':
        return parseFloat(cellStr) < parseFloat(filterStr);
      case 'lessThanOrEqual':
        return parseFloat(cellStr) <= parseFloat(filterStr);
      case 'isEmpty':
        return cellStr === '' || cellValue === null || cellValue === undefined;
      case 'isNotEmpty':
        return cellStr !== '' && cellValue !== null && cellValue !== undefined;
      default:
        return cellStr.includes(filterStr);
    }
  }

  /**
   * Reset all state
   */
  resetState() {
    this.sorting.set([]);
    this.columnFilters.set([]);
    this.globalFilter.set('');
    this.pagination.set({ pageIndex: 0, pageSize: this.initialPageSize() });
    this.rowSelection.set({});
    this.columnVisibility.set({});
    this.columnOrder.set([]);
    this.columnPinning.set({ left: [], right: [] });
    this.columnSizing.set({});
    this.expanded.set({});
    this.grouping.set([]);
    this.rowPinning.set({ top: [], bottom: [] });
  }

  /**
   * Export table data to file
   * @param format Export format: 'csv', 'json', or 'excel'
   * @param filename Optional filename (default: 'table-export')
   */
  exportData(format: 'csv' | 'json' | 'excel' = 'csv', filename: string = 'table-export') {
    const rows = this.table.getFilteredRowModel().rows;
    const data = rows.map((row) => row.original);
    const columns = this.table.getAllLeafColumns().filter(col => col.id !== 'select' && col.id !== 'expander');

    switch (format) {
      case 'csv':
        this.exportAsCSV(data, columns, filename);
        break;
      case 'json':
        this.exportAsJSON(data, filename);
        break;
      case 'excel':
        console.warn('Excel export requires xlsx library. Falling back to CSV.');
        this.exportAsCSV(data, columns, filename);
        break;
      default:
        console.error(`Unknown export format: ${format}`);
    }

    return data;
  }

  /**
   * Export data as CSV
   */
  private exportAsCSV(data: TData[], columns: any[], filename: string) {
    if (data.length === 0) {
      console.warn('No data to export');
      return;
    }

    // Get column headers
    const headers = columns.map(col => {
      const header = col.columnDef.header;
      return typeof header === 'string' ? header : col.id;
    });

    // Get column accessors
    const accessors = columns.map(col => {
      if (col.columnDef.accessorKey) {
        return (row: any) => row[col.columnDef.accessorKey];
      } else if (col.columnDef.accessorFn) {
        return col.columnDef.accessorFn;
      } else {
        return () => '';
      }
    });

    // Build CSV content
    const csvRows: string[] = [];

    // Add header row
    csvRows.push(headers.map(h => this.escapeCSV(h)).join(','));

    // Add data rows
    data.forEach(row => {
      const values = accessors.map(accessor => {
        const value = accessor(row);
        return this.escapeCSV(value);
      });
      csvRows.push(values.join(','));
    });

    const csvContent = csvRows.join('\n');
    this.downloadFile(csvContent, `${filename}.csv`, 'text/csv;charset=utf-8;');
  }

  /**
   * Export data as JSON
   */
  private exportAsJSON(data: TData[], filename: string) {
    if (data.length === 0) {
      console.warn('No data to export');
      return;
    }

    const jsonContent = JSON.stringify(data, null, 2);
    this.downloadFile(jsonContent, `${filename}.json`, 'application/json;charset=utf-8;');
  }

  /**
   * Escape CSV value
   */
  private escapeCSV(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    const stringValue = String(value);

    // If value contains comma, quote, or newline, wrap in quotes and escape quotes
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }

    return stringValue;
  }

  /**
   * Trigger file download
   */
  private downloadFile(content: string, filename: string, mimeType: string) {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Get depth-based padding for hierarchical rows
   * Only applies padding to the first data column
   */
  getDepthPadding(depth: number, columnId: string): string | null {
    if (!this.enableExpanding() || depth === 0) {
      return null;
    }

    // Find the first data column (not select, not expander)
    const visibleCols = this.visibleColumns();
    let firstDataColumnId: string | null = null;

    for (const col of visibleCols) {
      if (col.id !== 'select' && col.id !== 'expander') {
        firstDataColumnId = col.id;
        break;
      }
    }

    // Only apply indentation to the first data column
    if (columnId !== firstDataColumnId) {
      return null;
    }

    // Base padding (16px) + depth padding (24px per level)
    const basePadding = 16;
    const depthPadding = depth * 24;
    return `${basePadding + depthPadding}px`;
  }

  /**
   * Get header context for rendering
   */
  getHeaderContext(column: any) {
    // Find the header for this column
    const headerGroups = this.table.getHeaderGroups();
    for (const group of headerGroups) {
      const header = group.headers.find((h) => h.column.id === column.id);
      if (header) {
        return header.getContext();
      }
    }
    // Fallback context
    return {
      column,
      header: { column, getContext: () => ({ column, table: this.table }) } as any,
      table: this.table,
    };
  }

  // ========================================
  // Column Ordering - Drag & Drop
  // ========================================

  /**
   * Handle column drag start
   */
  onColumnDragStart(event: DragEvent, columnId: string) {
    if (!this.enableColumnOrdering()) {
      return;
    }
    this.draggedColumnId.set(columnId);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', columnId);
    }
  }

  /**
   * Handle column drag over
   */
  onColumnDragOver(event: DragEvent, columnId: string) {
    if (!this.enableColumnOrdering() || !this.draggedColumnId()) {
      return;
    }
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    this.dropTargetColumnId.set(columnId);
  }

  /**
   * Handle column drag leave
   */
  onColumnDragLeave(event: DragEvent) {
    if (!this.enableColumnOrdering()) {
      return;
    }
    this.dropTargetColumnId.set(null);
  }

  /**
   * Handle column drop
   */
  onColumnDrop(event: DragEvent, targetColumnId: string) {
    if (!this.enableColumnOrdering()) {
      return;
    }
    event.preventDefault();

    const sourceColumnId = this.draggedColumnId();
    if (!sourceColumnId || sourceColumnId === targetColumnId) {
      this.draggedColumnId.set(null);
      this.dropTargetColumnId.set(null);
      return;
    }

    // Get current column order (or all leaf column ids if no custom order set)
    const currentOrder = this.columnOrder();
    const allColumns = this.table.getAllLeafColumns();
    const columnIds = currentOrder.length > 0
      ? currentOrder
      : allColumns.map(col => col.id);

    // Find indices
    const sourceIndex = columnIds.indexOf(sourceColumnId);
    const targetIndex = columnIds.indexOf(targetColumnId);

    if (sourceIndex === -1 || targetIndex === -1) {
      this.draggedColumnId.set(null);
      this.dropTargetColumnId.set(null);
      return;
    }

    // Reorder array
    const newOrder = [...columnIds];
    const [removed] = newOrder.splice(sourceIndex, 1);
    newOrder.splice(targetIndex, 0, removed);

    // Update state
    this.columnOrder.set(newOrder);

    // Clear drag state
    this.draggedColumnId.set(null);
    this.dropTargetColumnId.set(null);
  }

  /**
   * Handle column drag end
   */
  onColumnDragEnd(event: DragEvent) {
    this.draggedColumnId.set(null);
    this.dropTargetColumnId.set(null);
  }
}
